import { isHTTPError, isNetworkError } from 'ky';
import { useCloudflareChallengeStore } from '../../stores/cloudflare-challenge';

// Cloudflare answers a challenged call with 403 or 429 and this header.
// The browser only exposes both when the answer carries CORS headers,
// otherwise the call fails as a network error.
const CHALLENGE_STATUSES = new Set([403, 429]);
const CHALLENGE_HEADER = 'cf-mitigated';

const getSpaces = () => Object.values(window.CONFIG?.member?.scope?.spaces ?? {});

// the space whose url prefixes the call url and has the option on
export const getChallengeSpace = url => {
  if (typeof url !== 'string') return undefined;
  return getSpaces().find(
    ({ cloudflareChallenge, url: spaceUrl, urlv3 }) =>
      !!cloudflareChallenge?.verifyUrl &&
      [spaceUrl, urlv3].some(prefix => !!prefix && url.startsWith(prefix)),
  );
};

// ky wraps a failed fetch in a NetworkError, but it recognises one by its
// message, so a bare fetch TypeError counts too
export const isChallenge = error => {
  if (isHTTPError(error)) {
    const { status, headers } = error.response;
    return CHALLENGE_STATUSES.has(status) && headers.get(CHALLENGE_HEADER) === 'challenge';
  }
  return isNetworkError(error) || error?.name === 'TypeError';
};

// one round per dialog: every challenged call waits on the same promise
let round = null;

const waitForVerification = space => {
  if (!round) {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    round = { promise, resolve, reject };
    useCloudflareChallengeStore.setState({ space });
  }
  return round.promise;
};

// called by the dialog: Continue settles with true, Cancel with false
export const settleChallenge = isVerified => {
  if (!round) return;
  const { resolve, reject } = round;
  round = null;
  useCloudflareChallengeStore.setState({ space: null });
  if (isVerified) resolve();
  else reject();
};

// A challenged call waits for the user, then is sent again once.
// Nothing is re-sent without a click, so a challenge can't loop.
export const withChallenge = (url, call) => {
  const space = getChallengeSpace(url);
  if (!space) return call();
  return call().catch(error => {
    if (!isChallenge(error)) throw error;
    return waitForVerification(space).then(
      () => withChallenge(url, call),
      () => {
        throw error;
      },
    );
  });
};
